import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { withAuth } from '@/components/auth-wrapper';
import { DealsTable } from '@/components/deals-list/deals-table';
import { InternalGuard } from '@/components/internal-guard';
import { ChooseLogo } from '@/components/ui/choose-logo';
import { useDealsListUrlSync } from '@/hooks/use-deals-list-url';
import { useUser } from '@/hooks/use-user';
import { parseDealsListParams } from '@/lib/deals-list-url';
import { trackEvent } from '@/lib/mixpanel';
import { dealsListState } from '@/states/deals-list.state';
import { Provider, createStore } from 'jotai';

export function PublicHomePage() {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = t('page.home.title');
  }, [t]);

  useEffect(() => {
    trackEvent('HOME_VIEWED');
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background to-muted">
      <div className="text-center">
        <div className="mb-6 flex justify-center">
          <ChooseLogo width={90} height={90} />
        </div>
        <h1
          data-testid="choose-title"
          className="text-4xl font-bold tracking-tight"
        >
          {t('page.home.title')}
        </h1>
      </div>
    </div>
  );
}

const InternalHomePage = withAuth(function InternalHomePage() {
  const user = useUser();
  const userId = user?.providerUser.sub;
  const [params] = useSearchParams();
  const session = useRef<{
    userId: string | undefined;
    store: ReturnType<typeof createStore>;
  }>();
  if (!session.current || session.current.userId !== userId) {
    const store = createStore();
    // Explicit URLs seed a new visit; changing accounts resets the current filters.
    if (!session.current?.userId)
      store.set(dealsListState, parseDealsListParams(params));
    session.current = { userId, store };
  }
  return (
    <InternalGuard fallback={<PublicHomePage />}>
      <Provider key={userId} store={session.current.store}>
        <DealsList userId={userId} />
      </Provider>
    </InternalGuard>
  );
});

function DealsList({ userId }: { userId: string | undefined }) {
  useDealsListUrlSync(userId);
  return <DealsTable />;
}

export default function HomePage() {
  return <InternalHomePage />;
}
